import { useCallback, useState } from 'react'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import createProfileSegments from 'api/administration/lms-profiles/segments/create'
import destroyManyProfileSegments from 'api/administration/lms-profiles/segments/destroyMany'
import { ErrorToast, SuccessToast } from 'components/ui/toast/toastsTemplates'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { useEditProfile } from 'pages/configuration/LmsProfiles/pages/EditProfile/context'
import { Segment } from 'models/segment.model'
import { applyFilters } from 'helpers/filterData.helper'

interface UseActionsProps {
  allSelected: boolean
  filterData?: IFilterData
  search?: string
}

export const useActions = ({
  allSelected,
  filterData,
  search,
}: UseActionsProps) => {
  const t = useTranslationRankmi({
    prefix: 'administration.toastMessages.profile.users',
  })
  const {
    profile,
    form: { trigger },
  } = useEditProfile()
  const [addLoading, setAddLoading] = useState(false)
  const [removeLoading, setRemoveLoading] = useState(false)

  const handleChanges = useCallback(async () => {
    trigger()
  }, [trigger])

  const onAdd = useCallback(
    async (newSegments: Segment[]) => {
      if (!profile) return
      setAddLoading(true)
      const options = {
        count: newSegments.length,
      }
      try {
        await createProfileSegments(
          profile.id,
          newSegments.map(({ id }) => id)
        )
        handleChanges()
        SuccessToast(t('createSuccess', options))
      } catch {
        ErrorToast(t('createError', options))
      }
      setAddLoading(false)
    },
    [handleChanges, profile, t]
  )

  const onRemove = useCallback(
    async (...ids: number[]) => {
      if (!profile) return
      setRemoveLoading(true)
      const options = {
        count: ids.length,
      }

      const commonQuery = { name_cont: search, g: applyFilters(filterData) }
      try {
        await destroyManyProfileSegments(profile.id, {
          ...commonQuery,
          ...(allSelected ? { id_not_in: ids } : { id_in: ids }),
        })
        handleChanges()
        SuccessToast(t('deleteSuccess', options))
      } catch {
        ErrorToast(t('deleteError', options))
      }
      setRemoveLoading(false)
    },
    [allSelected, filterData, handleChanges, profile, search, t]
  )

  return {
    onAdd,
    loading: {
      add: addLoading,
      remove: removeLoading,
    },
    onRemove,
  }
}
